import { useMemo } from 'react';
import { Roles } from '../../constants/roles';
import { UserOrganization } from '../../constants/userOrganization';
import { QuickFilters } from '../../interfaces/quickFilters/QuickFilters';
import {
  CasemanagerESPConfig,
  CasemanagerIntegraConfig,
  DefaultConfig,
  ReferralUserConfig,
  UnderwriterConfig,
  UwxConfig,
} from './quickFiltersConfig';

const isInternalWorkbenchRole = (role: unknown): role is Roles => {
  switch (role) {
    case Roles.WORKBENCH_ADMIN:
    case Roles.WORKBENCH_UWA:
    case Roles.WORKBENCH_SENIOR_OPS_UNDERWRITER:
    case Roles.WORKBENCH_STRATEGY_REVIEWER:
    case Roles.WORKBENCH_UNDERWRITER:
    case Roles.WORKBENCH_JUNIOR_UNDERWRITER:
    case Roles.WORKBENCH_READ_ONLY:
      return true;
    default:
      return false;
  }
};

export const PickRoleForQuickFilters = (roles: string[]): Roles => {
  for (const role of roles) {
    if (isInternalWorkbenchRole(role)) {
      return role;
    }

    switch (role) {
      case Roles.REFERRAL:
      case Roles.UNDERWRITER:
      case Roles.CHIEF_UNDERWRITER:
      case Roles.PRODUCTION_UNDERWRITER:
      case Roles.UW_JUNIOR_UNDERWRITER:
      case Roles.CASEMANAGER:
      case Roles.CHIEF_CASEMANAGER:
      case Roles.UWX_TEAM:
      case Roles.UW_UNDERWRITING_MANAGER:
        return role;
    }
  }

  // if we can't find any roles above, default to "read only role" which will hide the quickFilters
  return Roles.READ_ONLY;
};

export const GetQuickFilters = (
  role: Roles,
  userOrganization: UserOrganization,
  canViewTasksManagement: boolean,
): QuickFilters => {
  let config: QuickFilters;

  if (isInternalWorkbenchRole(role)) {
    config = canViewTasksManagement ? CasemanagerIntegraConfig : DefaultConfig;
  } else {
    switch (role) {
      case Roles.UNDERWRITER:
      case Roles.CHIEF_UNDERWRITER:
      case Roles.PRODUCTION_UNDERWRITER:
      case Roles.UW_JUNIOR_UNDERWRITER:
      case Roles.UW_UNDERWRITING_MANAGER:
        config = UnderwriterConfig;
        break;
      case Roles.CASEMANAGER:
      case Roles.CHIEF_CASEMANAGER:
        if (userOrganization === UserOrganization.INTEGRA)
          config = CasemanagerIntegraConfig;
        else if (userOrganization === UserOrganization.ESP)
          config = CasemanagerESPConfig;
        else config = CasemanagerIntegraConfig;
        break;
      case Roles.UWX_TEAM:
        config = UwxConfig;
        break;
      case Roles.REFERRAL:
        config = ReferralUserConfig;
        break;
      default:
        config = DefaultConfig;
    }
  }

  // If new layout is enabled, slice the first section from config
  // This is because the first section is the legacy application sidebar
  return {
    ...config,
    section: config.section.slice(1),
  };
};

export const useQuickFilters = (
  role: Roles,
  userOrganization: UserOrganization,
  sectionIdx: number,
  filterIdx: number,
  canViewTasksManagement: boolean,
) => {
  const quickFilters = useMemo(
    () => GetQuickFilters(role, userOrganization, canViewTasksManagement),
    [role, userOrganization, canViewTasksManagement],
  );

  return {
    filterType:
      quickFilters?.section[sectionIdx]?.list[filterIdx]?.quickFilterType,
    taskQuery: quickFilters?.section[sectionIdx]?.list[filterIdx]?.queryBody,
    taskViewTitle: quickFilters?.section[sectionIdx]?.list[filterIdx]?.title,
  };
};
